import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setProfileViewing } from '../../../Redux/store';
import ProfileRow from '../../../components/ProfileRow';
import TestRow from '../../../components/TestRow';
import useTestsOthers from '../../../hooks/useTestsOthers';
import useProfileData from '../../../hooks/useProfileData';
import useCalendarDataOther from '../../../hooks/useCalendarDataOther';
import { getLocalTodayString } from '../../../utils/dateFormat';

function ScheduleRow({ prediction }) {
  const dispatch = useDispatch();
  const currentUserId = useSelector((s) => s.user.authUserId);
  const currentProfile = useSelector((s) => s.user.profile);

  const { ownerId, ownerName, ownerImages, ovulationActual, ovulationPrediction, date } = prediction;

  // Get tests for this user
  const { tests } = useTestsOthers(ownerId);

  // Get calendar data for cycle day lookup
  const { calendarData: otherCalendarData } = useCalendarDataOther(ownerId);

  // Get other user's profile for location data
  const { profile: otherProfile } = useProfileData(ownerId);

  // Get highest test from today or previous day
  const getHighestTest = () => {
    if (!tests || tests.length === 0) return null;

    const today = getLocalTodayString();
    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterday = `${yesterdayDate.getFullYear()}-${String(yesterdayDate.getMonth() + 1).padStart(2, '0')}-${String(yesterdayDate.getDate()).padStart(2, '0')}`;

    // Filter tests from today or yesterday - convert UTC to local for comparison
    const recentTests = tests.filter(test => {
      const localDate = new Date(test.taken_at);
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      const testDate = `${year}-${month}-${day}`;
      return testDate === today || testDate === yesterday;
    });

    if (recentTests.length === 0) return null;

    // Sort by value descending and return highest
    recentTests.sort((a, b) => (b.value || 0) - (a.value || 0));
    return recentTests[0];
  };

  const highestTest = getHighestTest();

  // Calculate border opacity based on highest test value (like calendar button)
  const testOpacity = highestTest?.value !== undefined && highestTest?.value !== null
    ? Math.min(1, Math.max(0.05, highestTest.value / 2))
    : 0.3; // Default opacity if no test

  const borderStyle = { borderColor: `rgba(147, 51, 234, ${testOpacity})` };

  // Get location text from profile
  const locationText = otherProfile?.city && otherProfile?.state
    ? `${otherProfile.city}, ${otherProfile.state}`
    : (otherProfile?.location || '');

  const subtitle = ovulationActual
    ? 'Actual ovulation'
    : (ovulationPrediction ? 'Predicted ovulation' : 'Ovulation');

  const profile = {
    id: ownerId,
    name: ownerName,
    images: ownerImages,
  };

  return (
    <li
      onClick={() => dispatch(setProfileViewing({ id: ownerId, tab: 'calendar' }))}
      className={`flex items-center rounded-xl p-3 cursor-pointer transition-colors ${
        ovulationActual
          ? 'bg-purple-100 hover:bg-purple-200'
          : 'bg-secondary hover:bg-secondary/80'
      }`}
    >
      <div className="flex-1">
        <ProfileRow
          profile={profile}
          size="md"
          subtitle={`${subtitle}${locationText ? ` • ${locationText}` : ''}`}
          profileStyle="purple"
          customBorder={borderStyle}
        />
        {highestTest && (
          <div className="mt-2">
            <TestRow test={highestTest} allTests={tests} calendarData={otherCalendarData} />
          </div>
        )}
      </div>
    </li>
  );
}

export default ScheduleRow;
